import { formatNumber } from '@rhino-project/core/i18n/format';
import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { compact } from 'lodash';
import {
  useModelCreate,
  useModelDelete,
  useModelIndex,
  useModelUpdate
} from '@rhino-project/core/hooks';
import {
  defaultCountries,
  parseCountry,
  FlagImage
} from 'react-international-phone';
import type { ParsedCountry } from 'react-international-phone';
import type { RhinoRead } from '@rhino-project/core';

type Player = RhinoRead<'player'>;

// A position is rendered by name, shortened to its abbreviation when the names
// run long.
type ChipPosition = Pick<
  Player['positions'][number],
  'id' | 'name' | 'abbreviation'
>;

// The chips are given a player from a show query and a player nested in a
// roster, which carry the same positions, and the specs give one with neither.
type ChipPlayer = Pick<Player, 'id'> & {
  positions?: ChipPosition[];
  player_positions?: { id: number; position: ChipPosition }[];
};

// The stat row forwards the fields its chips render on to each of them.
type StatChipPlayer = ChipPlayer &
  Partial<
    Pick<
      Player,
      | 'hand'
      | 'quick_rating'
      | 'dob_year'
      | 'grad_year'
      | 'height_feet'
      | 'height_inches'
      | 'weight_lbs'
      | 'hometown'
      | 'country'
      | 'number'
    >
  >;

type ChipSize = 'xs' | 'sm';

interface SelectOption {
  value: string;
  label: string;
}

// Chip style constants
export const chipAccent =
  'inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-accent/15 text-accent border border-accent/30';
export const chipWarning =
  'inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-warning/15 text-warning border border-warning/30 font-medium';

interface InlineEditChipProps {
  value: string | number | null | undefined;
  label?: string;
  field: string;
  playerId: number | string | undefined;
  type?: string;
  options?: SelectOption[];
  isAdmin?: boolean;
  onSaved?: () => void;
  size?: ChipSize;
}

export const InlineEditChip = ({
  value,
  label,
  field,
  playerId,
  type = 'text',
  options,
  isAdmin,
  onSaved,
  size = 'sm'
}: InlineEditChipProps) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>(String(value ?? ''));
  const inputRef = useRef<HTMLSelectElement | HTMLInputElement>(null);
  const { mutate: updatePlayer } = useModelUpdate('player');

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      (inputRef.current as HTMLInputElement).select?.();
    }
  }, [editing]);

  const handleSave = useCallback(() => {
    const newValue = editValue === '' ? null : editValue;
    if (newValue !== (value ?? null)) {
      updatePlayer(
        { id: playerId as number, [field]: newValue } as unknown as Parameters<
          typeof updatePlayer
        >[0],
        { onSuccess: onSaved }
      );
    }
    setEditing(false);
  }, [editValue, value, field, playerId, updatePlayer, onSaved]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter') handleSave();
      if (e.key === 'Escape') {
        setEditValue(String(value ?? ''));
        setEditing(false);
      }
    },
    [handleSave, value]
  );

  const sizeClasses =
    size === 'xs' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-0.5 text-sm';

  if (!isAdmin && !value) return null;

  if (editing) {
    if (options) {
      return (
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
          }}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={`rounded-full ${sizeClasses} bg-surface border border-separator focus:outline-none focus:ring-1 focus:ring-focus`}
        >
          <option value="">--</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type={type}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`rounded-full ${sizeClasses} bg-surface border border-separator focus:outline-none focus:ring-1 focus:ring-focus w-20`}
      />
    );
  }

  if (!value && !isAdmin) return null;

  if (!value && isAdmin) {
    return (
      <button
        type="button"
        onClick={() => {
          setEditValue('');
          setEditing(true);
        }}
        className={`inline-flex items-center ${sizeClasses} rounded-full border border-dashed border-separator text-muted cursor-pointer hover:bg-surface-secondary`}
      >
        + {label || field}
      </button>
    );
  }

  const displayValue = label ? `${label} ${value}` : value;

  return (
    <button
      type="button"
      onClick={() => {
        if (isAdmin) {
          setEditValue(String(value ?? ''));
          setEditing(true);
        }
      }}
      className={`inline-flex items-center ${sizeClasses} rounded-full bg-accent/15 text-accent border border-accent/30 ${
        isAdmin ? 'cursor-pointer hover:bg-accent/25' : 'cursor-default'
      }`}
    >
      {displayValue}
    </button>
  );
};

// Height chip — two-input inline editor for height_feet + height_inches
interface HeightChipProps {
  heightFeet: number | string | null | undefined;
  heightInches: number | string | null | undefined;
  playerId: number | string | undefined;
  isAdmin?: boolean;
  onSaved?: () => void;
  size?: ChipSize;
}

export const HeightChip = ({
  heightFeet,
  heightInches,
  playerId,
  isAdmin,
  onSaved,
  size = 'sm'
}: HeightChipProps) => {
  const { t } = useTranslation();

  const [editing, setEditing] = useState(false);
  const [editFeet, setEditFeet] = useState('');
  const [editInches, setEditInches] = useState('');
  const feetRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { mutate: updatePlayer } = useModelUpdate('player');

  useEffect(() => {
    if (editing && feetRef.current) {
      feetRef.current.focus();
      feetRef.current.select();
    }
  }, [editing]);

  const handleSave = useCallback(() => {
    const newFeet = editFeet === '' ? null : editFeet;
    const rawInches =
      editInches === '' ? null : Math.min(11, Math.max(0, Number(editInches)));
    const newInches = rawInches == null ? null : String(rawInches);
    if (
      String(newFeet ?? '') !== String(heightFeet ?? '') ||
      String(newInches ?? '') !== String(heightInches ?? '')
    ) {
      updatePlayer(
        {
          id: playerId as number,
          height_feet: newFeet,
          height_inches: newInches
        } as unknown as Parameters<typeof updatePlayer>[0],
        { onSuccess: onSaved }
      );
    }
    setEditing(false);
  }, [
    editFeet,
    editInches,
    heightFeet,
    heightInches,
    playerId,
    updatePlayer,
    onSaved
  ]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (containerRef.current?.contains(e.relatedTarget as Node)) return;
      handleSave();
    },
    [handleSave]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSave();
      if (e.key === 'Escape') {
        setEditFeet(String(heightFeet ?? ''));
        setEditInches(String(heightInches ?? ''));
        setEditing(false);
      }
    },
    [handleSave, heightFeet, heightInches]
  );

  const sizeClasses =
    size === 'xs' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-0.5 text-sm';

  const hasValue = heightFeet != null;

  if (!isAdmin && !hasValue) return null;

  if (editing) {
    return (
      <div ref={containerRef} className="inline-flex items-center gap-0.5">
        <input
          ref={feetRef}
          type="number"
          value={editFeet}
          onChange={(e) => setEditFeet(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="ft"
          className={`rounded-full ${sizeClasses} bg-surface border border-separator focus:outline-none focus:ring-1 focus:ring-focus w-12 text-center`}
        />
        <span className="text-muted text-xs">&prime;</span>
        <input
          type="number"
          min={0}
          max={11}
          value={editInches}
          onChange={(e) => setEditInches(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="in"
          className={`rounded-full ${sizeClasses} bg-surface border border-separator focus:outline-none focus:ring-1 focus:ring-focus w-12 text-center`}
        />
        <span className="text-muted text-xs">&Prime;</span>
      </div>
    );
  }

  if (!hasValue && isAdmin) {
    return (
      <button
        type="button"
        onClick={() => {
          setEditFeet('');
          setEditInches('');
          setEditing(true);
        }}
        className={`inline-flex items-center ${sizeClasses} rounded-full border border-dashed border-separator text-muted cursor-pointer hover:bg-surface-secondary`}
      >
        {t('frontend.messages.height_2')}
      </button>
    );
  }

  const displayValue = compact([heightFeet, heightInches]).join("'");

  return (
    <button
      type="button"
      onClick={() => {
        if (isAdmin) {
          setEditFeet(String(heightFeet ?? ''));
          setEditInches(String(heightInches ?? ''));
          setEditing(true);
        }
      }}
      className={`inline-flex items-center ${sizeClasses} rounded-full bg-accent/15 text-accent border border-accent/30 ${
        isAdmin ? 'cursor-pointer hover:bg-accent/25' : 'cursor-default'
      }`}
    >
      {t('frontend.messages.height')} {displayValue}
    </button>
  );
};

interface PositionsChipProps {
  player: ChipPlayer | undefined;
  isAdmin?: boolean;
  onSaved?: () => void;
  size?: ChipSize;
}

export const PositionsChip = ({
  player,
  isAdmin,
  onSaved,
  size = 'sm'
}: PositionsChipProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const { resources: allPositions } = useModelIndex('position', { limit: 100 });
  const { mutate: createPlayerPosition } = useModelCreate('player_position');
  const { mutate: deletePlayerPosition } = useModelDelete('player_position');

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // positions is a has_many through player_positions, so the two carry the same
  // records; the join is preferred because deletion needs its identifiers.
  const currentPositionIds = useMemo(() => {
    const playerPositions = player?.player_positions;
    if (playerPositions?.length) {
      return new Set(playerPositions.map((pp) => pp.position.id));
    }
    const positions = player?.positions;
    if (positions?.length) {
      return new Set(positions.map((p) => p.id));
    }
    return new Set<number>();
  }, [player]);

  const playerPositionMap = useMemo(() => {
    const playerPositions = player?.player_positions;
    if (playerPositions?.length) {
      return Object.fromEntries(
        playerPositions.map((pp) => [pp.position.id, pp.id])
      );
    }
    // No join table IDs available -- deletion will need a lookup
    return {} as Record<string, number>;
  }, [player]);

  const handleToggle = useCallback(
    (positionId: number) => {
      if (currentPositionIds.has(positionId)) {
        const ppId = playerPositionMap[positionId];
        if (ppId) deletePlayerPosition(ppId, { onSuccess: onSaved });
      } else {
        createPlayerPosition(
          {
            player: player?.id,
            position: positionId
          } as unknown as Parameters<typeof createPlayerPosition>[0],
          { onSuccess: onSaved }
        );
      }
    },
    [
      currentPositionIds,
      playerPositionMap,
      player,
      createPlayerPosition,
      deletePlayerPosition,
      onSaved
    ]
  );

  const positions = player?.positions;
  const positionsFull = positions
    ?.map((p) => p.name as string)
    .join(' \u00b7 ');
  const positionsShort = positions
    ?.map((p) => p.abbreviation as string)
    .join(' \u00b7 ');
  const positionText =
    (positionsFull?.length ?? 0) > 20 ? positionsShort : positionsFull;

  if (!positionText && !isAdmin) return null;

  const sizeClasses =
    size === 'xs' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-0.5 text-sm';

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => isAdmin && setOpen(!open)}
        className={`inline-flex items-center ${sizeClasses} rounded-full ${
          positionText
            ? `bg-accent/15 text-accent border border-accent/30 ${
                isAdmin ? 'hover:bg-accent/25' : ''
              }`
            : 'border border-dashed border-separator text-muted hover:bg-surface-secondary'
        } ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {positionText || t('frontend.messages.position_2')}
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="absolute top-full left-0 mt-1 bg-surface border border-separator rounded-3xl shadow-surface z-50 py-1 min-w-[140px]"
        >
          {(
            allPositions as { results: RhinoRead<'position'>[] } | undefined
          )?.results?.map((pos) => (
            <label
              key={pos.id as number}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-secondary cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={currentPositionIds.has(pos.id)}
                onChange={() => handleToggle(pos.id)}
                className="rounded"
              />
              {(pos.name || pos.display_name) as string}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

interface RatingChipProps {
  rating: string | number | null | undefined;
  size?: ChipSize;
}

export const RatingChip = ({ rating, size = 'sm' }: RatingChipProps) => {
  useTranslation();

  if (!rating) return null;
  const sizeClasses =
    size === 'xs' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-0.5 text-sm';
  return (
    <span
      className={`inline-flex items-center ${sizeClasses} rounded-full bg-rating/15 text-rating border border-rating/30 font-medium`}
    >
      ★{' '}
      {formatNumber(Number(rating), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}
    </span>
  );
};

// Country chip
interface CountryOptionProps {
  c: ParsedCountry;
  selected: boolean;
  onSelect: (iso2: string) => void;
}

const CountryOption = ({ c, selected, onSelect }: CountryOptionProps) => (
  <button
    type="button"
    onClick={() => onSelect(c.iso2)}
    className={`flex items-center gap-2 w-full px-3 py-1.5 hover:bg-surface-secondary text-sm text-left ${
      selected ? 'bg-surface-secondary' : ''
    }`}
  >
    <FlagImage className="size-4" iso2={c.iso2} />
    {c.name}
  </button>
);

interface CountryChipProps {
  country: string | null | undefined;
  playerId: number | string | undefined;
  isAdmin?: boolean;
  onSaved?: () => void;
}

export const CountryChip = ({
  country,
  playerId,
  isAdmin,
  onSaved
}: CountryChipProps) => {
  const { t, locale } = useTranslation();
  const parsedCountries = useMemo(() => {
    const names = new Intl.DisplayNames([locale.replaceAll('_', '-')], {
      type: 'region'
    });
    return defaultCountries
      .map((c) => {
        const country = parseCountry(c);
        return {
          ...country,
          name: names.of(country.iso2.toUpperCase()) ?? country.name
        };
      })
      .sort((a, b) =>
        a.name.localeCompare(b.name, locale.replaceAll('_', '-'))
      );
  }, [locale]);
  const pinnedCountries = parsedCountries.filter(
    (c) => c.iso2 === 'ca' || c.iso2 === 'us'
  );
  const restCountries = parsedCountries.filter(
    (c) => c.iso2 !== 'ca' && c.iso2 !== 'us'
  );

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { mutate: updatePlayer } = useModelUpdate('player');

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search) return null;
    const q = search.toLowerCase();
    return parsedCountries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.iso2.includes(q)
    );
  }, [search, parsedCountries]);

  const handleSelect = useCallback(
    (iso2: string) => {
      const newValue = iso2.toUpperCase();
      if (newValue !== (country ?? null)) {
        updatePlayer(
          {
            id: playerId as number,
            country: newValue
          } as unknown as Parameters<typeof updatePlayer>[0],
          { onSuccess: onSaved }
        );
      }
      setOpen(false);
      setSearch('');
    },
    [country, playerId, updatePlayer, onSaved]
  );

  if (!country && !isAdmin) return null;

  const currentCountry = country
    ? parsedCountries.find((c) => c.iso2 === country.toLowerCase())
    : null;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => isAdmin && setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-sm ${
          country
            ? `bg-accent/15 text-accent border border-accent/30 ${
                isAdmin ? 'cursor-pointer hover:bg-accent/25' : 'cursor-default'
              }`
            : 'border border-dashed border-separator text-muted cursor-pointer hover:bg-surface-secondary'
        }`}
      >
        {currentCountry ? (
          <>
            <FlagImage className="size-4" iso2={currentCountry.iso2} />
            {currentCountry.name}
          </>
        ) : (
          t('frontend.messages.country')
        )}
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="absolute top-full left-0 mt-1 bg-surface border border-separator rounded-3xl shadow-surface z-50 w-56"
        >
          <div className="p-2">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('frontend.messages.search_countries')}
              className="w-full rounded-md border border-separator bg-surface px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-focus"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered ? (
              filtered
                .slice(0, 50)
                .map((c) => (
                  <CountryOption
                    key={c.iso2}
                    c={c}
                    selected={country?.toLowerCase() === c.iso2}
                    onSelect={handleSelect}
                  />
                ))
            ) : (
              <>
                {pinnedCountries.map((c) => (
                  <CountryOption
                    key={c.iso2}
                    c={c}
                    selected={country?.toLowerCase() === c.iso2}
                    onSelect={handleSelect}
                  />
                ))}
                <div className="border-b border-separator my-1" />
                {restCountries.map((c) => (
                  <CountryOption
                    key={c.iso2}
                    c={c}
                    selected={country?.toLowerCase() === c.iso2}
                    onSelect={handleSelect}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Convenience component: renders a standard set of player stat chips
interface PlayerStatChipsProps {
  player: StatChipPlayer | undefined;
  playerId: number | string | undefined;
  isAdmin?: boolean;
  onSaved?: () => void;
  size?: ChipSize;
}

export const PlayerStatChips = ({
  player,
  playerId,
  isAdmin,
  onSaved,
  size = 'sm'
}: PlayerStatChipsProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap gap-1 items-center">
      <PositionsChip
        player={player}
        isAdmin={isAdmin}
        onSaved={onSaved}
        size={size}
      />
      <InlineEditChip
        value={player?.hand}
        label={t('frontend.messages.hand')}
        field="hand"
        playerId={playerId}
        isAdmin={isAdmin}
        onSaved={onSaved}
        size={size}
        options={[
          { value: 'L', label: t('frontend.messages.left') },
          { value: 'R', label: t('frontend.messages.right') }
        ]}
      />
      <RatingChip rating={player?.quick_rating} size={size} />
      <InlineEditChip
        value={player?.dob_year}
        label={t('frontend.messages.born')}
        field="dob_year"
        playerId={playerId}
        type="number"
        isAdmin={isAdmin}
        onSaved={onSaved}
        size={size}
      />
      <InlineEditChip
        value={player?.grad_year}
        label={t('frontend.messages.grad')}
        field="grad_year"
        playerId={playerId}
        type="number"
        isAdmin={isAdmin}
        onSaved={onSaved}
        size={size}
      />
      <HeightChip
        heightFeet={player?.height_feet}
        heightInches={player?.height_inches}
        playerId={playerId}
        isAdmin={isAdmin}
        onSaved={onSaved}
        size={size}
      />
      <InlineEditChip
        value={player?.weight_lbs ? `${player.weight_lbs}` : null}
        label={t('frontend.messages.weight')}
        field="weight_lbs"
        playerId={playerId}
        type="number"
        isAdmin={isAdmin}
        onSaved={onSaved}
        size={size}
      />
      <InlineEditChip
        value={player?.hometown}
        label={t('frontend.messages.hometown')}
        field="hometown"
        playerId={playerId}
        isAdmin={isAdmin}
        onSaved={onSaved}
        size={size}
      />
      <CountryChip
        country={player?.country}
        playerId={playerId}
        isAdmin={isAdmin}
        onSaved={onSaved}
      />
    </div>
  );
};
